import classNames from 'classnames';
import React from 'react';

import { useTextField } from '@react-aria/textfield';

import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';

import { ICON_POSITION } from 'click-controls/constant/enums';

import ClickFieldError from './common/error';
import type { IProps as IClickFieldErrorProps } from './common/error';
import ClickFieldIcon from './common/icon';
import type { IProps as IClickFieldIconProps } from './common/icon';
import ClickFieldInputContainer from './common/input-container';
import ClickFieldLabel from './common/label';
import type { IProps as IClickFieldLabelProps } from './common/label';

export interface IProps
  extends IFieldProps,
    Pick<IClickFieldLabelProps, 'name' | 'required' | 'onReset'> {
  inputContainerClass?: string;
  labelClass?: string;
  minLength?: number;
  placeholder?: string;
  value?: string;
  validationRegEx?: RegExp;
  errorTextClassName?: IClickFieldErrorProps['className'];
  errorText?: IClickFieldErrorProps['value'];
  allowPaste?: boolean;

  icon?: IClickFieldIconProps['children'];
  iconBorder?: IClickFieldIconProps['border'];
  iconPadding?: IClickFieldIconProps['padding'];
  iconPosition?: IClickFieldIconProps['position'];

  resetBtn?: IClickFieldLabelProps['resetable'];
  onClick?: React.ChangeEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
}

const Field: React.FC<IProps> = React.memo((props) => {
  const {
    className,
    disabled,
    errorTextClassName,
    errorText,
    label,
    labelClass,
    icon,
    iconBorder,
    iconPadding,
    iconPosition,
    inputContainerClass,
    maxLength,
    minLength,
    name,
    required,
    resetBtn,
    submitted,
    type,
    validationRegEx,
    allowPaste = true,
    onChange,
    onClick,
    onKeyUp,
    onReset
  } = props;

  const inputRef = React.useRef(null);
  const { labelProps, inputProps } = useTextField(
    { ...props.bzOmit('onChange'), isDisabled: props.disabled },
    inputRef
  );

  const onKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === 'tel' && !/[0-9 ()+-]/.test(event.key)) {
        event.preventDefault();
      }
      if (!(validationRegEx?.test(event.key) ?? true)) {
        event.preventDefault();
      }
    },
    [type, validationRegEx]
  );

  const onPaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (!allowPaste) {
        event.preventDefault();
      }
    },
    [allowPaste]
  );
  return (
    <div className="field-container">
      <ClickFieldLabel
        {...labelProps.bzOmit('onReset')}
        className={labelClass}
        label={label}
        name={name}
        required={required}
        resetable={resetBtn}
        onReset={onReset}
      />
      <ClickFieldInputContainer className={inputContainerClass}>
        <input
          className={classNames('click-input pl-2', className, {
            'mb-0': !!errorText && submitted,
            'pl-10': iconPosition == ICON_POSITION.Left,
            'pr-10': iconPosition == ICON_POSITION.Right
          })}
          {...inputProps}
          name={name ?? label}
          placeholder={inputProps.placeholder ?? label}
          ref={inputRef}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          type={type}
          onChange={onChange}
          onKeyPress={onKeyPress}
          onKeyUp={onKeyUp}
          onClick={onClick}
          onPaste={onPaste}
        />
        {!!icon && (
          <ClickFieldIcon border={iconBorder} padding={iconPadding} position={iconPosition}>
            {icon}
          </ClickFieldIcon>
        )}
      </ClickFieldInputContainer>
      {!!errorText && submitted && (
        <ClickFieldError className={errorTextClassName} value={errorText} />
      )}
    </div>
  );
});

Field.displayName = 'Field';

export default Field;
